import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Building2, ArrowRight, Check } from 'lucide-react';

const AccountTypePrelude = ({ onAccountTypeSelected }) => {
  const [selectedType, setSelectedType] = useState(null);

  const accountTypes = [
    {
      id: 'individual',
      title: 'Individual',
      description: "I'm an individual or creator who promotes brands across my own media properties",
      icon: User,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      hoverColor: 'hover:border-blue-300',
      selectedColor: 'border-blue-500 bg-blue-50'
    },
    {
      id: 'company',
      title: 'Company',
      description: "I'm a member of/represent a company who promotes brands across its media properties",
      icon: Building2,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      hoverColor: 'hover:border-purple-300',
      selectedColor: 'border-purple-500 bg-purple-50'
    }
  ];

  const handleTypeSelect = (type) => {
    setSelectedType(type);
  };

  const handleContinue = () => {
    if (selectedType) {
      onAccountTypeSelected(selectedType);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6 }}
      className="py-8 mx-auto w-full max-w-4xl"
    >
      {/* Header */}
      <div className="mb-12 text-center">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-4 text-3xl font-bold text-gray-900"
        >
          Choose Your Account Type
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mx-auto max-w-2xl text-lg text-gray-600"
        >
          Select the account type that best describes you. This will customize your registration experience.
        </motion.p>
      </div>

      {/* Account Type Selection */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        {accountTypes.map((type, index) => {
          const IconComponent = type.icon;
          const isSelected = selectedType === type.id;
          
          return (
            <motion.div
              key={type.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              onClick={() => handleTypeSelect(type.id)}
              className={`relative cursor-pointer transition-all duration-300 ${
                isSelected 
                  ? `${type.selectedColor} shadow-lg shadow-${type.color.split('-')[1]}-500/20` 
                  : `${type.bgColor} ${type.borderColor} ${type.hoverColor}`
              } border-2 rounded-xl p-6 hover:shadow-md`}
            >
              {/* Selection Indicator */}
              <div className="absolute top-4 right-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: isSelected ? 1 : 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    isSelected 
                      ? `bg-gradient-to-r ${type.color} text-white` 
                      : 'bg-gray-200 border-2 border-gray-300'
                  }`}
                >
                  {isSelected && <Check className="w-4 h-4" />}
                </motion.div>
              </div>

              {/* Content */}
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg bg-gradient-to-r ${type.color} text-white shadow-lg`}>
                  <IconComponent className="w-6 h-6" />
                </div>
                
                <div className="flex-1">
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">
                    {type.title}
                  </h3>
                  <p className="leading-relaxed text-gray-600">
                    {type.description}
                  </p>
                </div>
              </div>

              {/* Hover Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r rounded-xl opacity-0 transition-opacity duration-300 hover:opacity-5"
                style={{
                  background: `linear-gradient(135deg, ${type.color.includes('blue') ? '#3B82F6' : '#8B5CF6'}, ${type.color.includes('blue') ? '#1D4ED8' : '#7C3AED'})`
                }}
              />
            </motion.div>
          );
        })}
      </div>

      {/* Continue Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-center"
      >
        <motion.button
          onClick={handleContinue}
          disabled={!selectedType}
          whileHover={{ scale: selectedType ? 1.02 : 1 }}
          whileTap={{ scale: selectedType ? 0.98 : 1 }}
          className={`inline-flex items-center px-8 py-4 space-x-2 text-lg font-medium rounded-lg transition-all duration-200 ${
            selectedType
              ? 'text-white bg-gradient-to-r from-red-500 to-red-600 shadow-lg shadow-red-500/25 hover:from-red-600 hover:to-red-700 hover:shadow-xl hover:shadow-red-500/40'
              : 'text-gray-400 bg-gray-100 cursor-not-allowed'
          }`}
        >
          <span>Continue</span>
          <ArrowRight className="w-5 h-5" />
        </motion.button>
        
        {!selectedType && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-2 text-sm text-gray-500"
          >
            Please select an account type to continue
          </motion.p>
        )}
      </motion.div>

      {/* Additional Info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="p-6 mt-12 bg-gray-50 rounded-xl border border-gray-200"
      >
        <h4 className="mb-3 text-lg font-semibold text-gray-900">
          What's the difference?
        </h4>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h5 className="flex items-center mb-2 font-medium text-gray-900">
              <User className="mr-2 w-4 h-4 text-blue-500" />
              Individual Account
            </h5>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Perfect for content creators and influencers</li>
              <li>• Simplified registration process</li>
              <li>• Direct brand partnerships</li>
              <li>• Personal tax information</li>
            </ul>
          </div>
          <div>
            <h5 className="flex items-center mb-2 font-medium text-gray-900">
              <Building2 className="mr-2 w-4 h-4 text-purple-500" />
              Company Account
            </h5>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Ideal for agencies and businesses</li>
              <li>• Additional company verification</li>
              <li>• Team management features</li>
              <li>• Business tax information</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AccountTypePrelude;
